'use client';

import { Suspense, useEffect, useState, useRef, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import toast from 'react-hot-toast';
import styles from '../styles/Dashboard.module.css';
import adminStyles from '../styles/AdminDashboard.module.css';
import { AgentChatWidget } from '../components/AgentChatWidget/AgentChatWidget';
import Stage2Camera from '../components/Stage2Camera';
import DateTimePicker from '../components/DateTimePicker';
import { computeDescriptorsFromImages } from '@/lib/face-descriptors';
import { useModalBackdropDismiss } from '@/lib/useModalBackdropDismiss';
import {
  computeInterviewSchedulingDeadline,
  isInterviewSchedulingExpired,
} from '@/lib/interview-scheduling-window';
import { useSessionExpiry } from '@/lib/useSessionExpiry';

const STAGE_STEPS = [
  {
    id: 1,
    title: 'Interview Details',
    subtitle: 'Candidate & slot information',
  },
  {
    id: 2,
    title: 'Candidate Image',
    subtitle: 'Candidate photo capture',
  },
] as const;

function DashboardFallback() {
  return (
    <main className={styles.page}>
      <div className={styles.shell}>
        <div className={styles.loginLayout}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h1 className={styles.cardTitle}>Loading…</h1>
              <p className={styles.cardSubtitle}>Please wait.</p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function DashboardContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [loading, setLoading] = useState(false);
  type CandidateTab = 'overview' | 'current' | 'image';
  const [activeTab, setActiveTab] = useState<CandidateTab>('overview');
  const [detailsCompleted, setDetailsCompleted] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const closeUserMenu = useCallback(() => setShowUserMenu(false), []);

  interface CandidateSummary {
    id: string;
    name: string | null;
    email: string;
    gender: string | null;
    mobile: string | null;
    total_experience_years: number | null;
    relevant_experience_years: number | null;
    technical_skills: string[] | null;
    certifications: string[] | null;
    current_location: string | null;
    current_company: string | null;
    gap_in_service: string | null;
    notice_period_days: number | null;
    current_ctc: number | null;
    expected_ctc: number | null;
    ctc_currency: string | null;
    status: string | null;
    resume_path: string | null;
    face_capture_path?: string | null;
    resume_url?: string | null;
  }

  interface CandidateInterview {
    id: string;
    candidate_id: string;
    position_id: string | null;
    scheduled_time: string | null;
    status: string | null;
    created_at: string;
    meeting_room_id?: string | null;
    meeting_link?: string | null;
    time_change_status?: 'pending' | 'approved' | 'rejected' | 'timed_out' | null;
    time_change_requested_time?: string | null;
    candidate_time_zone?: string | null;
    position?: { id: string; title: string | null } | null;
  }

  const [candidate, setCandidate] = useState<CandidateSummary | null>(null);
  const [interview, setInterview] = useState<CandidateInterview | null>(null);
  const [interviews, setInterviews] = useState<CandidateInterview[]>([]);
  const [loadingInterview, setLoadingInterview] = useState(false);
  const [joiningInterview, setJoiningInterview] = useState(false);
  const [requestingTimeChange, setRequestingTimeChange] = useState(false);
  const [requestedDateTime, setRequestedDateTime] = useState<Date | null>(null);
  const [showTimeChangePanel, setShowTimeChangePanel] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const closeCancelModal = useCallback(() => {
    setShowCancelModal(false);
    setCancelReason('');
  }, []);
  const cancelInterviewModalDismiss = useModalBackdropDismiss(closeCancelModal);

  // Schedule form state (step 1)
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null);
  const [capturedImages, setCapturedImages] = useState<string[]>([]);
  const [capturedDescriptors, setCapturedDescriptors] = useState<number[][]>([]);
  const resumeInputRef = useRef<HTMLInputElement>(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [adminCheckDone, setAdminCheckDone] = useState(false);
  const [resumeUrlLoading, setResumeUrlLoading] = useState(false);

  useEffect(() => {
    if (!showUserMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        closeUserMenu();
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showUserMenu, closeUserMenu]);

  // Check authentication status on mount
  useEffect(() => {
    checkSession();
  }, []);

  // Detect session expiry while the tab is open or when the user returns to it.
  useSessionExpiry(isAuthenticated === true);

  // Show toast when redirected back from room (e.g. session expired, outside time window)
  useEffect(() => {
    const roomError = searchParams.get('room_error');
    if (!roomError) return;
    const messages: Record<string, string> = {
      session_missing: 'Please sign in again to join the interview.',
      session_invalid: 'Your session expired. Please sign in again.',
      booking_required: 'Invalid link. Please open the interview from your dashboard.',
      booking_not_found: 'Interview not found or no longer available.',
      booking_not_confirmed: 'This interview is not confirmed yet.',
      pending_approval: 'This interview is still pending approval from HR.',
      interview_completed: 'This interview has already been completed.',
      interview_cancelled: 'This interview has been cancelled.',
      not_authorized: 'You are not authorized to join this interview.',
      booking_room_mismatch: 'This interview room link is invalid or has changed. Please use the Join button from your dashboard.',
      outside_time_window: 'You can join from 15 minutes before until 60 minutes after your interview time.',
      admin_outside_time_window: 'Admins can only access the room from 15 minutes before until 60 minutes after the scheduled time.',
      interview_not_live: 'Candidate has not yet joined the interview.',
      invalid_time: 'Invalid interview time. Please contact support.',
      agent_timeout: 'The interview agent did not connect in time. Please try joining again.',
    };
    const message = messages[roomError] || 'Could not enter the interview. Please try again.';
    toast.error(message);
    router.replace('/', { scroll: false });
  }, [searchParams, router]);

  // Fetch candidate + interview when authenticated (initial load)
  useEffect(() => {
    if (isAuthenticated) {
      fetchCandidateInterview();
    }
  }, [isAuthenticated]);

  // Check admin status whenever authentication state changes; redirect admins to /admin immediately
  useEffect(() => {
    if (!isAuthenticated) {
      setIsAdminUser(false);
      setAdminCheckDone(false);
      return;
    }

    let cancelled = false;
    setAdminCheckDone(false);

    const checkRole = async () => {
      try {
        const roleResp = await fetch('/api/auth/check-role', {
          credentials: 'include',
        });
        if (!roleResp.ok) {
          if (!cancelled) {
            setIsAdminUser(false);
            setAdminCheckDone(true);
          }
          return;
        }
        const roleData = await roleResp.json();
        if (!cancelled) {
          setIsAdminUser(!!roleData.isAdmin);
          setAdminCheckDone(true);
          if (roleData.isAdmin) {
            router.replace('/admin');
          } else if (roleData.isManager) {
            router.replace('/hiring-review');
          }
        }
      } catch {
        if (!cancelled) {
          setIsAdminUser(false);
          setAdminCheckDone(true);
        }
      }
    };

    checkRole();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, router]);

  // Background refresh every 30 seconds without affecting UX
  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    const refreshData = async () => {
      try {
        // Only refresh when tab is visible to avoid unnecessary work
        if (typeof document !== 'undefined' && document.hidden) {
          return;
        }

        const resp = await fetch('/api/candidate/interview', { credentials: 'include' });
        if (!cancelled && resp.ok) {
          const data = await resp.json();
          setCandidate(data.candidate ?? null);
          setInterview(data.interview ?? null);
          setInterviews(Array.isArray(data.interviews) ? data.interviews : []);
        }
      } catch {
        // Ignore background refresh errors to avoid disrupting the user
      }
    };

    const intervalId = setInterval(refreshData, 30000); // 30 seconds

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  const checkSession = async () => {
    try {
      const response = await fetch('/api/auth/session', {
        credentials: 'include',
      });
      const data = await response.json();
      setIsAuthenticated(data.authenticated);
      if (data.authenticated) {
        setEmail(data.email);
      }
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const fetchCandidateInterview = async () => {
    setLoadingInterview(true);
    try {
      const response = await fetch('/api/candidate/interview', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setCandidate(data.candidate ?? null);
        setInterview(data.interview ?? null);
        setInterviews(Array.isArray(data.interviews) ? data.interviews : []);
      } else {
        setCandidate(null);
        setInterview(null);
        setInterviews([]);
      }
    } catch (error) {
      console.error('Error fetching candidate interview:', error);
      setCandidate(null);
      setInterview(null);
      setInterviews([]);
    } finally {
      setLoadingInterview(false);
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'OTP sent! Check console for development.');
        setShowOtpInput(true);
      } else {
        toast.error(data.error || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleUseAnotherEmail = () => {
    setShowOtpInput(false);
    setOtp('');
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otp) {
      toast.error('Please enter the OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        toast.success('Login successful!');
        // Re-check session to ensure cookie is set
        await checkSession();
        setShowOtpInput(false);
        setOtp('');

        // Check if user is admin
        const adminCheck = await fetch('/api/auth/check-admin', {
          credentials: 'include',
        });
        const adminData = await adminCheck.json();
        setIsAdminUser(!!adminData.isAdmin);
        if (adminData.isAdmin) {
          router.push('/admin');
          return;
        }
      } else {
        toast.error(data.error || 'Invalid OTP');
      }
    } catch (error) {
      toast.error('Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      setIsAuthenticated(false);
      setEmail('');
      setCandidate(null);
      setInterview(null);
      setShowOtpInput(false);
      setIsAdminUser(false);
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const resetScheduleForm = () => {
    setSelectedDateTime(null);
    setResumeFile(null);
    setCapturedImages([]);
    setCapturedDescriptors([]);
    if (resumeInputRef.current) {
      resumeInputRef.current.value = '';
    }
  };

  const handleScheduleDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!interview) {
      toast.error('No interview available to schedule.');
      return;
    }
    if (!selectedDateTime) {
      toast.error('Please select a date and time');
      return;
    }
    toast.success('Details saved! Now capture your photo.');
    setDetailsCompleted(true);
    setActiveTab('image');
  };

  const formatDateTime = (dateTimeString: string | null) => {
    if (!dateTimeString) return 'Not scheduled yet';
    try {
      return new Date(dateTimeString).toLocaleString();
    } catch {
      return dateTimeString;
    }
  };

  const formatTimeChangeStatus = (status: string | null | undefined): string => {
    if (!status) return 'No request';
    return status
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  const goBackToStage1 = () => {
    if (!hasCurrentInterview || currentStatus === 'approved' || currentStatus === 'in_progress') {
      setActiveTab('overview');
      return;
    }
    setActiveTab('current');
  };

  const handleStage2Complete = async (images?: string[]) => {
    // Prevent duplicate submissions
    if (loading) {
      return;
    }

    const imagesToUse = images || capturedImages;

    if (!interview) {
      toast.error('No interview available to schedule.');
      return;
    }

    if (!selectedDateTime) {
      toast.error('Missing interview time. Please go back and pick a date/time.');
      return;
    }

    if (!imagesToUse || imagesToUse.length === 0) {
      toast.error('No images captured. Please try again.');
      return;
    }

    setLoading(true);
    try {
      let descriptors = capturedDescriptors;
      if (!descriptors || descriptors.length === 0) {
        descriptors = await computeDescriptorsFromImages(imagesToUse);
      }
      if (descriptors.length === 0) {
        toast('Could not extract face data from images. Proceeding without face descriptors.');
      } else if (descriptors.length < imagesToUse.length) {
        toast('Some images had no detectable face. Proceeding with available data.');
      }

      const formData = new FormData();
      formData.append('interviewId', interview.id);
      // Send a full ISO string (UTC) so the timestamptz column stores an exact instant.
      formData.append('scheduledTime', selectedDateTime.toISOString());
      const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTimeZone) {
        formData.append('candidateTimeZone', browserTimeZone);
      }
      formData.append('faceDescriptors', JSON.stringify(descriptors));
      if (resumeFile) {
        formData.append('resume', resumeFile);
      }

      imagesToUse.forEach((base64Image, index) => {
        const base64Data = base64Image.split(',')[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/jpeg' });
        formData.append('faceImages', blob, `face-${index}.jpg`);
      });

      const response = await fetch('/api/candidate/interview', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Interview scheduled successfully!');
        resetScheduleForm();
        setDetailsCompleted(false);
        setActiveTab('overview');
        // Re-fetch from canonical source so overview data is never briefly null
        await fetchCandidateInterview();
      } else {
        if (response.status === 409 || response.status === 400) {
          await fetchCandidateInterview();
        }
        toast.error(data.error || 'Failed to schedule interview');
      }
    } catch (error) {
      console.error('Error scheduling interview:', error);
      toast.error('Failed to schedule interview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestTimeChange = async () => {
    if (!interview || !requestedDateTime) {
      toast.error('Please choose a requested date/time first.');
      return;
    }
    setRequestingTimeChange(true);
    try {
      const candidateTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await fetch('/api/candidate/interview/time-change/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          interviewId: interview.id,
          requestedTime: requestedDateTime.toISOString(),
          candidateTimeZone,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 409 || response.status === 400) {
          await fetchCandidateInterview();
        }
        toast.error(data.error || 'Failed to submit time change request');
        return;
      }
      toast.success('Time change request submitted.');
      setRequestedDateTime(null);
      setShowTimeChangePanel(false);
      await fetchCandidateInterview();
    } catch (error) {
      console.error('Error requesting time change:', error);
      toast.error('Failed to submit time change request');
    } finally {
      setRequestingTimeChange(false);
    }
  };

  // Loading state
  if (isAuthenticated === null) {
    return (
      <main className={styles.page}>
        <div className={styles.shell}>
          <div className={styles.loginLayout}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <h1 className={styles.cardTitle}>Checking your session…</h1>
                <p className={styles.cardSubtitle}>Please wait a moment while we prepare your dashboard.</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Login form
  if (!isAuthenticated) {
    return (
      <main className={styles.loginPage}>
        <div className={styles.loginFormPanel}>
          <div className={styles.loginBrandRow}>
            <Image
              src="/images/10xds-logo.svg"
              alt="10xDS"
              width={100}
              height={48}
              className={styles.loginBrandLogo}
              priority
            />
          </div>
          <div className={styles.loginFormCard}>
            <div className={styles.loginFormAccentBar} />
            <div className={styles.loginFormCardInner}>
            <div className={styles.loginFormHeader}>
              <div className={styles.loginFormIconWrap}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20.5899 22C20.5899 18.13 16.7399 15 11.9999 15C7.25991 15 3.40991 18.13 3.40991 22" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h2 className={styles.loginFormTitle}>
                  {showOtpInput ? 'Check your email' : 'Welcome back'}
                </h2>
                <p className={styles.loginFormSubtitle}>
                  {showOtpInput
                    ? `We sent a 6-digit code to ${email}`
                    : 'Sign in to access your interview dashboard'}
                </p>
              </div>
            </div>

            <form className={styles.form} onSubmit={showOtpInput ? handleVerifyOTP : handleSendOTP}>
              <div className={styles.field}>
                <label htmlFor="email" className={styles.label}>Email address</label>
                <div className={styles.inputIconWrap}>
                  <svg className={styles.inputIcon} width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 6L12 13L2 6" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={showOtpInput}
                    className={`${styles.input} ${styles.inputWithIcon}`}
                    placeholder="you@email.com"
                  />
                </div>
              </div>

              {showOtpInput && (
                <div className={styles.field}>
                  <label htmlFor="otp" className={styles.label}>One-time passcode</label>
                  <div className={styles.inputIconWrap}>
                    <svg className={styles.inputIcon} width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="11" width="18" height="11" rx="2" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input
                      id="otp"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      required
                      maxLength={6}
                      className={`${styles.input} ${styles.inputWithIcon} ${styles.inputOtp}`}
                      placeholder="• • • • • •"
                      inputMode="numeric"
                      autoFocus
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleUseAnotherEmail}
                    disabled={loading}
                    className={styles.linkButton}
                  >
                    ← Use a different email
                  </button>
                </div>
              )}

              <button type="submit" disabled={loading} className={styles.primaryButton}>
                {loading
                  ? 'Please wait…'
                  : showOtpInput
                  ? 'Verify & Sign in'
                  : 'Send one-time code'}
              </button>
            </form>

            {!showOtpInput && (
              <p className={styles.loginDisclaimer}>
                You will receive a one time pass code to your email.
              </p>
            )}
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Authenticated but still checking admin, or admin (redirecting) - show loading so we never flash the candidate dashboard
  if (isAuthenticated && (!adminCheckDone || isAdminUser)) {
    return (
      <main className={styles.page}>
        <div className={styles.shell}>
          <div className={styles.loginLayout}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <h1 className={styles.cardTitle}>
                  {isAdminUser ? 'Redirecting to admin…' : 'Checking your session…'}
                </h1>
                <p className={styles.cardSubtitle}>
                  {isAdminUser ? 'Taking you to the admin dashboard.' : 'Please wait a moment while we prepare your dashboard.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  const pageHeadings: Record<CandidateTab, { title: string; description: string }> = {
    overview: {
      title: 'Overview',
      description: 'Your profile and interview overview.',
    },
    current: {
      title: 'Schedule Interview',
      description: 'Schedule and manage your upcoming interview.',
    },
    image: {
      title: 'Capture Face ID',
      description: 'Capture your photo for the interview.',
    },
  };

  // Derived interview state for UI
  const currentStatus = interview?.status ?? null;
  const isMeetingOpenStatus = currentStatus === 'approved' || currentStatus === 'in_progress';
  const scheduleDeadline = interview?.created_at
    ? computeInterviewSchedulingDeadline(interview.created_at)
    : null;
  const isSchedulingWindowExpired =
    !!interview?.created_at && isInterviewSchedulingExpired(interview.created_at);
  const shouldMoveInterviewToPrevious =
    !!interview &&
    !isMeetingOpenStatus &&
    currentStatus !== 'completed' &&
    currentStatus !== 'cancelled' &&
    isSchedulingWindowExpired;
  const hasCurrentInterview =
    !!interview &&
    currentStatus !== 'completed' &&
    currentStatus !== 'cancelled' &&
    !shouldMoveInterviewToPrevious;

  const previousInterviews = interviews.filter((item) =>
    hasCurrentInterview ? item.id !== interview!.id : true
  );
  const isInterviewUiExpired = (item: CandidateInterview): boolean => {
    const status = (item.status ?? '').toLowerCase();
    if (status === 'completed' || status === 'cancelled' || status === 'approved' || status === 'in_progress') {
      return false;
    }
    if (!item.created_at) return false;
    return isInterviewSchedulingExpired(item.created_at);
  };
  const scheduleDeadlineLabel = scheduleDeadline
    ? scheduleDeadline.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      })
    : null;

  function getStatusChipClass(status: string | null): string {
    const s = (status ?? '').toLowerCase();
    if (s === 'pending') return styles.statusChipPending;
    if (s === 'approved') return styles.statusChipApproved;
    if (s === 'completed') return styles.statusChipCompleted;
    if (s === 'cancelled') return styles.statusChipCancelled;
    return styles.statusChip;
  }

  const scheduledDate = interview?.scheduled_time ? new Date(interview.scheduled_time) : null;
  const now = typeof window !== 'undefined' ? new Date() : null;
  const joinWindowStart =
    scheduledDate && new Date(scheduledDate.getTime() - 15 * 60 * 1000); // 15 min before
  const joinWindowEnd =
    scheduledDate && new Date(scheduledDate.getTime() + 60 * 60 * 1000); // 1 hour after

  const isJoinWindowOpen =
    !!scheduledDate &&
    isMeetingOpenStatus &&
    !!now &&
    !!joinWindowStart &&
    !!joinWindowEnd &&
    now >= joinWindowStart &&
    now <= joinWindowEnd;

  const isBeforeJoinWindow =
    !!scheduledDate && !!now && !!joinWindowStart && now < joinWindowStart;

  const isAfterJoinWindow = !!scheduledDate && !!now && !!joinWindowEnd && now > joinWindowEnd;
  const rescheduleAvailableAgainTitle =
    isJoinWindowOpen && joinWindowEnd
      ? `Time change requests will be available again at ${joinWindowEnd.toLocaleString()}.`
      : undefined;

  const joinButtonLabel = (() => {
    if (isJoinWindowOpen) {
      return joiningInterview ? 'Joining...' : 'Join interview';
    }
    if (isBeforeJoinWindow) {
      return 'Join interview (available closer to start time)';
    }
    if (isAfterJoinWindow) {
      return 'Interview time has passed';
    }
    return 'Join interview (available closer to start time)';
  })();

  const handleViewResume = async () => {
    if (resumeUrlLoading) return;
    setResumeUrlLoading(true);
    try {
      const res = await fetch('/api/candidate/resume-url', {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('[CandidatePage] Error fetching resume URL', error);
    } finally {
      setResumeUrlLoading(false);
    }
  };

  // Dashboard (authenticated, admin check done, not an admin)
  return (
    <div className={adminStyles.pageAdmin}>
      <div className={adminStyles.adminLayout}>
        <aside className={adminStyles.adminSidebar} aria-label="Dashboard navigation">
          <div className={adminStyles.adminSidebarLogo}>
            <Image
              src="/images/10xds-logo.svg"
              alt="10xDS Logo"
              width={120}
              height={58}
              priority
            />
          </div>
          <nav className={adminStyles.adminSidebarNav}>
            <button
              type="button"
              className={
                activeTab === 'overview'
                  ? `${adminStyles.adminSidebarItem} ${adminStyles.adminSidebarItemActive}`
                  : adminStyles.adminSidebarItem
              }
              onClick={() => setActiveTab('overview')}
            >
              <span className={adminStyles.adminSidebarItemLabel}>Overview</span>
            </button>
            <button
              type="button"
              className={
                activeTab === 'current'
                  ? `${adminStyles.adminSidebarItem} ${adminStyles.adminSidebarItemActive}`
                  : adminStyles.adminSidebarItem
              }
              onClick={() => {
                if (!hasCurrentInterview) {
                  toast.error('You do not have a current interview scheduled.');
                  return;
                }
                if (isMeetingOpenStatus) {
                  toast.error('Your interview is already approved. You can join from the Overview tab.');
                  return;
                }
                setActiveTab('current');
              }}
              disabled={!hasCurrentInterview || isMeetingOpenStatus}
              style={
                !hasCurrentInterview || isMeetingOpenStatus
                  ? { opacity: 0.45, cursor: 'not-allowed' }
                  : undefined
              }
            >
              <span className={adminStyles.adminSidebarItemLabel}>Schedule Interview</span>
            </button>
            <button
              type="button"
              className={
                activeTab === 'image'
                  ? `${adminStyles.adminSidebarItem} ${adminStyles.adminSidebarItemActive}`
                  : adminStyles.adminSidebarItem
              }
              onClick={() => {
                if (!hasCurrentInterview) {
                  toast.error('You do not have a current interview scheduled.');
                  return;
                }
                if (!detailsCompleted) {
                  toast.error('Please fill in your interview details first.');
                  return;
                }
                setActiveTab('image');
              }}
              disabled={!hasCurrentInterview || !detailsCompleted}
              style={
                !hasCurrentInterview || !detailsCompleted
                  ? { opacity: 0.45, cursor: 'not-allowed' }
                  : undefined
              }
            >
              <span className={adminStyles.adminSidebarItemLabel}>Capture Face ID</span>
            </button>
          </nav>
          <div className={adminStyles.adminSidebarFooter} ref={userMenuRef}>
            <div
              className={adminStyles.adminSidebarUserTrigger}
              role="button"
              tabIndex={0}
              onClick={() => setShowUserMenu((prev) => !prev)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowUserMenu((prev) => !prev);
                }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className={adminStyles.adminSidebarUserEmail}>{email}</span>
            </div>
            {showUserMenu && (
              <div className={adminStyles.adminSidebarUserMenu}>
                <button
                  type="button"
                  className={adminStyles.adminSidebarUserMenuItemDanger}
                  onClick={() => {
                    closeUserMenu();
                    handleLogout();
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </aside>

        <div className={adminStyles.adminMain}>
          <header className={adminStyles.adminPageHeader}>
            <h1 className={adminStyles.adminPageTitle}>{pageHeadings[activeTab].title}</h1>
            <p className={adminStyles.adminPageDescription}>{pageHeadings[activeTab].description}</p>
          </header>
          <main className={adminStyles.adminTabContent}>
            {loadingInterview ? (
              <div className={styles.loadingBlock}>
                <div className={styles.loadingSpinner} />
                <p className={styles.loadingText}>Loading your interview...</p>
              </div>
            ) : !candidate || interviews.length === 0 ? (
              <div className={styles.emptyStateBox}>
                <p className={styles.emptyStateTitle}>No interview found</p>
                <p className={styles.emptyStateText}>
                  We couldn&apos;t find an interview linked to your email. Please contact HR if you
                  believe this is a mistake.
                </p>
              </div>
            ) : (
              <>
                {activeTab === 'overview' && candidate && (
                  <div className={styles.overviewSection}>
                    {hasCurrentInterview && (
                      <div className={`${styles.bookingCard} ${styles.bookingCardCurrent}`}>
                        <div className={styles.bookingTop}>
                          <div className={styles.bookingMeta}>
                            <p className={styles.interviewKicker}>Current interview</p>
                            <h3 className={styles.interviewRoleTitle}>
                              {interview.position?.title || 'Role not specified'}
                            </h3>
                            <div className={styles.bookingInfoGrid}>
                              <span className={styles.bookingLabel}>Scheduled</span>
                              <span className={styles.bookingValue}>
                                {formatDateTime(interview.scheduled_time)}
                              </span>
                              <span className={styles.bookingLabel}>Status</span>
                              <span>
                                <span
                                  className={`${styles.statusChip} ${getStatusChipClass(interview.status)}`}
                                >
                                  {(interview.status || 'pending').toUpperCase()}
                                </span>
                              </span>
                            </div>
                            {currentStatus === 'pending' && (
                              <p className={styles.bookingText} style={{ marginTop: '0.5rem' }}>
                                <span className={styles.bookingLabel}>Next step:&nbsp;</span>
                                Please choose a date and time and upload your resume (optional) to
                                confirm your slot.
                              </p>
                            )}
                            {scheduleDeadlineLabel && (
                              <p className={styles.bookingText} style={{ marginTop: '0.5rem' }}>
                                <span className={styles.bookingLabel}>Scheduling window:&nbsp;</span>
                                You can schedule until {scheduleDeadlineLabel}.
                              </p>
                            )}
                            {isSchedulingWindowExpired && !isMeetingOpenStatus && (
                              <p className={styles.bookingText} style={{ marginTop: '0.5rem' }}>
                                <span className={styles.bookingLabel}>Update:&nbsp;</span>
                                Your scheduling window has ended for this interview.
                              </p>
                            )}
                            {isMeetingOpenStatus && (
                              <p className={styles.bookingText} style={{ marginTop: '0.5rem' }}>
                                <span className={styles.bookingLabel}>Join window:&nbsp;</span>
                                You can join from 15 minutes before the scheduled time.
                              </p>
                            )}
                            <div className={styles.bookingActions}>
                              {currentStatus === 'pending' && (
                                <button
                                  type="button"
                                  className={styles.primaryButton}
                                  onClick={() => setActiveTab('current')}
                                >
                                  Schedule interview
                                </button>
                              )}
                              {isMeetingOpenStatus && (
                                <button
                                  type="button"
                                  className={styles.primaryButton}
                                  disabled={!isJoinWindowOpen || joiningInterview}
                                  onClick={() => {
                                    if (!isJoinWindowOpen || !interview || joiningInterview) return;
                                    const meetingRoomId = interview.meeting_room_id;
                                    if (!meetingRoomId) {
                                      toast.error(
                                        'Interview room not ready yet. Please try again later or contact HR.',
                                      );
                                      return;
                                    }
                                    setJoiningInterview(true);
                                    const roomName = encodeURIComponent(meetingRoomId);
                                    const bookingId = encodeURIComponent(interview.id);
                                    window.dispatchEvent(new Event('interview:join-start'));
                                    router.push(`/rooms/${roomName}?bookingId=${bookingId}`);
                                  }}
                                >
                                  {joinButtonLabel}
                                </button>
                              )}
                              {isMeetingOpenStatus && (
                                <button
                                  type="button"
                                  className={`${styles.secondaryButton} ${
                                    isJoinWindowOpen && !showTimeChangePanel ? styles.disabledSecondaryButton : ''
                                  }`}
                                  onClick={() => setShowTimeChangePanel((prev) => !prev)}
                                  disabled={isJoinWindowOpen && !showTimeChangePanel}
                                  title={
                                    isJoinWindowOpen && !showTimeChangePanel
                                      ? rescheduleAvailableAgainTitle
                                      : undefined
                                  }
                                >
                                  Request time change
                                </button>
                              )}
                              <button
                                type="button"
                                className={styles.dangerButton}
                                onClick={() => {
                                  setCancelReason('');
                                  setShowCancelModal(true);
                                }}
                              >
                                Cancel interview
                              </button>
                            </div>
                            {isMeetingOpenStatus && showTimeChangePanel && (
                              <section className={styles.timeChangeRequestCard}>
                                <div className={styles.timeChangeRequestHeader}>
                                  <p className={styles.timeChangeRequestTitle}>Request time change</p>
                                  <div className={styles.timeChangeRequestHeaderActions}>
                                    {interview.time_change_status && (
                                      <span
                                        className={`${styles.timeChangeRequestStatusChip} ${
                                          interview.time_change_status === 'pending'
                                            ? styles.timeChangeStatusPending
                                            : interview.time_change_status === 'approved'
                                              ? styles.timeChangeStatusApproved
                                              : interview.time_change_status === 'rejected'
                                                ? styles.timeChangeStatusRejected
                                                : styles.timeChangeStatusTimedOut
                                        }`}
                                      >
                                        {formatTimeChangeStatus(interview.time_change_status)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <p className={styles.timeChangeRequestPolicy}>
                                  Your requested time will only be effective if HR approves it.
                                </p>
                                {scheduleDeadlineLabel && (
                                  <p className={styles.timeChangeRequestPolicy}>
                                    You can schedule until {scheduleDeadlineLabel}.
                                  </p>
                                )}
                                {interview.time_change_requested_time && (
                                  <p className={styles.timeChangeRequestMeta}>
                                    Requested: {formatDateTime(interview.time_change_requested_time)}
                                  </p>
                                )}
                                <div className={styles.timeChangeRequestControls}>
                                  <div className={styles.timeChangePickerWrap}>
                                    <DateTimePicker
                                      selected={requestedDateTime}
                                      onChange={(d) => setRequestedDateTime(d)}
                                      minDate={new Date()}
                                      maxDate={scheduleDeadline ?? undefined}
                                      compact
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    className={`${styles.secondaryButton} ${
                                      isJoinWindowOpen ||
                                      requestingTimeChange ||
                                      interview.time_change_status === 'pending' ||
                                      isSchedulingWindowExpired ||
                                      !requestedDateTime
                                        ? styles.disabledSecondaryButton
                                        : ''
                                    }`}
                                    disabled={
                                      requestingTimeChange ||
                                      isJoinWindowOpen ||
                                      interview.time_change_status === 'pending' ||
                                      isSchedulingWindowExpired ||
                                      !requestedDateTime
                                    }
                                    onClick={handleRequestTimeChange}
                                    title={
                                      isJoinWindowOpen
                                        ? rescheduleAvailableAgainTitle
                                        : isSchedulingWindowExpired && scheduleDeadlineLabel
                                          ? `Reschedule window ended on ${scheduleDeadlineLabel}.`
                                        : undefined
                                    }
                                  >
                                    {requestingTimeChange
                                      ? 'Submitting request…'
                                      : interview.time_change_status === 'pending'
                                        ? 'Time change request pending'
                                        : 'Request time change'}
                                  </button>
                                </div>
                                {isJoinWindowOpen && (
                                  <p className={styles.timeChangeRequestHint}>
                                    Time change requests are disabled while join is available.
                                  </p>
                                )}
                                {!isJoinWindowOpen && isSchedulingWindowExpired && scheduleDeadlineLabel && (
                                  <p className={styles.timeChangeRequestHint}>
                                    Reschedule window ended on {scheduleDeadlineLabel}.
                                  </p>
                                )}
                              </section>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Previous interviews */}
                    <div className={styles.bookingCard}>
                      <div className={styles.bookingTop}>
                        <div className={styles.bookingMeta}>
                          <h3 className={styles.bookingTitle}>Previous interviews</h3>
                          <div className={styles.previousInterviewsList}>
                            {previousInterviews.length > 0 ? (
                              previousInterviews.map((item) => {
                                const isExpiredForUi = isInterviewUiExpired(item);
                                return (
                                <div key={item.id} className={styles.previousInterviewRow}>
                                  <div className={styles.previousInterviewMain}>
                                    <p className={styles.bookingText}>
                                      <span className={styles.bookingLabel}>Role:&nbsp;</span>
                                      {item.position?.title || 'Not specified'}
                                    </p>
                                    <p className={styles.bookingText}>
                                      <span className={styles.bookingLabel}>Scheduled:&nbsp;</span>
                                      {formatDateTime(item.scheduled_time)}
                                    </p>
                                  </div>
                                  <div className={styles.previousInterviewStatus}>
                                    <span
                                      className={`${styles.statusChip} ${
                                        isExpiredForUi ? styles.statusChipPending : getStatusChipClass(item.status)
                                      }`}
                                    >
                                      {isExpiredForUi ? 'EXPIRED' : (item.status || 'completed').toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                              );
                              })
                            ) : (
                              <p className={styles.helperText}>
                                You don&apos;t have any previous interviews yet.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Profile overview */}
                    <div className={styles.profileCard}>
                      <div className={styles.profileAccentBar} />

                      {/* Hero: avatar + identity + resume button */}
                      <div className={styles.profileHero}>
                        <div className={styles.profileAvatar} aria-hidden="true">
                          {(candidate.name || candidate.email)
                            .trim()
                            .split(/\s+/)
                            .map((w) => w[0] ?? '')
                            .join('')
                            .slice(0, 2)
                            .toUpperCase() || '?'}
                        </div>
                        <div className={styles.profileHeroInfo}>
                          <h3 className={styles.profileHeroName}>{candidate.name || '–'}</h3>
                          <p className={styles.profileHeroEmail}>{candidate.email}</p>
                          <div className={styles.profileHeroMeta}>
                            {candidate.current_company && (
                              <span className={styles.profileMetaChip}>
                                {candidate.current_company}
                              </span>
                            )}
                            {candidate.current_location && (
                              <span className={styles.profileMetaChip}>
                                {candidate.current_location}
                              </span>
                            )}
                            {candidate.mobile && (
                              <span className={styles.profileMetaChip}>{candidate.mobile}</span>
                            )}
                            {candidate.gender && (
                              <span className={styles.profileMetaChip}>{candidate.gender}</span>
                            )}
                          </div>
                        </div>
                        <div className={styles.profileHeroActions}>
                          {candidate.resume_path ? (
                            <button
                              type="button"
                              className={styles.profileViewResumeButton}
                              disabled={resumeUrlLoading}
                              onClick={handleViewResume}
                            >
                              {resumeUrlLoading ? 'Opening…' : 'View resume'}
                            </button>
                          ) : (
                            <span className={styles.helperText}>No resume uploaded</span>
                          )}
                        </div>
                      </div>

                      {/* Stats strip */}
                      <div className={styles.profileStatsRow}>
                        {(
                          [
                            {
                              label: 'Total exp',
                              value:
                                candidate.total_experience_years != null
                                  ? `${candidate.total_experience_years} yrs`
                                  : '–',
                            },
                            {
                              label: 'Relevant exp',
                              value:
                                candidate.relevant_experience_years != null
                                  ? `${candidate.relevant_experience_years} yrs`
                                  : '–',
                            },
                            {
                              label: 'Notice period',
                              value:
                                candidate.notice_period_days != null
                                  ? `${candidate.notice_period_days} days`
                                  : '–',
                            },
                            {
                              label: 'Current CTC',
                              value:
                                candidate.current_ctc != null
                                  ? `${candidate.ctc_currency || 'INR'} ${candidate.current_ctc}`
                                  : '–',
                            },
                            {
                              label: 'Expected CTC',
                              value:
                                candidate.expected_ctc != null
                                  ? `${candidate.ctc_currency || 'INR'} ${candidate.expected_ctc}`
                                  : '–',
                            },
                          ] as { label: string; value: string }[]
                        ).map(({ label, value }) => (
                          <div key={label} className={styles.profileStat}>
                            <span className={styles.profileStatValue}>{value}</span>
                            <span className={styles.profileStatLabel}>{label}</span>
                          </div>
                        ))}
                      </div>

                      {/* Technical skills */}
                      {candidate.technical_skills && candidate.technical_skills.length > 0 && (
                        <div className={styles.profileSection}>
                          <p className={styles.profileSectionLabel}>Technical skills</p>
                          <div className={styles.profileTagsWrap}>
                            {candidate.technical_skills.map((skill) => (
                              <span key={skill} className={styles.profileTag}>
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Certifications */}
                      {candidate.certifications && candidate.certifications.length > 0 && (
                        <div className={styles.profileSection}>
                          <p className={styles.profileSectionLabel}>Certifications</p>
                          <div className={styles.profileTagsWrap}>
                            {candidate.certifications.map((cert) => (
                              <span key={cert} className={styles.profileTag}>
                                {cert}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Gap in service */}
                      {candidate.gap_in_service && (
                        <div className={`${styles.profileSection} ${styles.profileSectionLast}`}>
                          <p className={styles.profileSectionLabel}>Gap in service</p>
                          <p className={styles.profileGapText}>{candidate.gap_in_service}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'current' && (
                  <div className={styles.newBookingCard}>
                    <div className={styles.stepper}>
                      {STAGE_STEPS.map((step) => (
                        <div
                          key={step.id}
                          className={`${styles.step} ${
                            step.id === 1 ? styles.stepActive : styles.stepDisabled
                          }`}
                        >
                          <div className={styles.stepBadge}>{step.id}</div>
                          <div className={styles.stepTexts}>
                            <span className={styles.stepTitle}>{step.title}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <form className={styles.form} onSubmit={handleScheduleDetailsSubmit}>
                      <div className={styles.field}>
                        <div className={styles.labelRow}>
                          <label htmlFor="scheduledTime" className={styles.label}>
                            Interview date & time *
                          </label>
                          <span className={styles.labelHint}>
                            Choose when you&apos;d like to attend
                          </span>
                        </div>
                        <DateTimePicker
                          selected={selectedDateTime}
                          onChange={(date) => setSelectedDateTime(date)}
                          minDate={new Date()}
                          maxDate={scheduleDeadline ?? undefined}
                        />
                        <p className={styles.helperText}>
                          Your interview will be scheduled in your local timezone
                          {scheduleDeadlineLabel ? ` and you can schedule until ${scheduleDeadlineLabel}.` : '.'}
                        </p>
                        {isSchedulingWindowExpired && (
                          <p className={styles.helperText}>
                            Scheduling window has ended for this interview.
                          </p>
                        )}
                      </div>

                      <div className={styles.field}>
                        <div className={styles.labelRow}>
                          <label htmlFor="resume" className={styles.label}>
                            Resume upload (optional)
                          </label>
                          <span className={styles.labelHint}>PDF or Word · up to 5MB</span>
                        </div>
                        <label htmlFor="resume" className={styles.fileLabel}>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 4V16M4 10H16"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                          {resumeFile ? resumeFile.name : 'Choose file'}
                        </label>
                        <input
                          ref={resumeInputRef}
                          id="resume"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setResumeFile(file);
                          }}
                          className={styles.fileInput}
                        />
                        {resumeFile && (
                          <p className={styles.helperText}>
                            Selected: {resumeFile.name}
                          </p>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={loading || isSchedulingWindowExpired}
                        className={styles.primaryButton}
                        title={
                          isSchedulingWindowExpired && scheduleDeadlineLabel
                            ? `Scheduling window ended on ${scheduleDeadlineLabel}.`
                            : undefined
                        }
                      >
                        {loading
                          ? 'Please wait…'
                          : isSchedulingWindowExpired
                            ? 'Scheduling window ended'
                            : 'Continue to face ID capture'}
                      </button>
                    </form>
                  </div>
                )}

                {activeTab === 'image' && (
                  <section className={styles.cardStage2}>
                    <div className={styles.stepper}>
                      <button
                        type="button"
                        className={`${styles.ghostButton} ${styles.stepBackButton}`}
                        onClick={goBackToStage1}
                        disabled={loading}
                      >
                        ←
                      </button>
                      {STAGE_STEPS.map((step) => (
                        <div
                          key={step.id}
                          className={`${styles.step} ${
                            step.id === 2 ? styles.stepActive : styles.stepDisabled
                          }`}
                        >
                          <div className={styles.stepBadge}>{step.id}</div>
                          <div className={styles.stepTexts}>
                            <span className={styles.stepTitle}>{step.title}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className={styles.stage2Content}>
                      <Stage2Camera
                        onBack={goBackToStage1}
                        onComplete={(images: string[]) => handleStage2Complete(images)}
                        onCaptureComplete={(images: string[], descriptors?: number[][]) => {
                          setCapturedImages(images);
                          if (descriptors) {
                            setCapturedDescriptors(descriptors);
                          }
                        }}
                        isLoading={loading}
                      />
                    </div>
                  </section>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Cancel interview modal */}
      {showCancelModal && (
        <div
          className={adminStyles.candidateModalOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-modal-title"
          {...cancelInterviewModalDismiss.overlayProps}
        >
          <div
            className={adminStyles.candidateModalContent}
            style={{ maxWidth: '28rem' }}
            {...cancelInterviewModalDismiss.panelPointerDownCaptureProps}
          >
            <div className={adminStyles.detailHeader}>
              <h2 id="cancel-modal-title" className={adminStyles.detailTitle} style={{ margin: 0 }}>
                Cancel interview
              </h2>
            </div>
            <div className={adminStyles.candidateModalBody}>
              <label htmlFor="cancel-reason" className={styles.bookingLabel} style={{ display: 'block', marginBottom: '0.35rem' }}>
                Reason for cancellation (required)
              </label>
              <textarea
                id="cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Please provide a reason for cancelling your interview."
                rows={4}
                className={styles.input}
                style={{ width: '100%', resize: 'vertical', padding: '0.5rem 0.75rem', minHeight: '5rem' }}
                disabled={cancelling}
              />
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={closeCancelModal}
                  disabled={cancelling}
                >
                  Back
                </button>
                <button
                  type="button"
                  className={styles.primaryButton}
                  disabled={!cancelReason.trim() || cancelling}
                  onClick={async () => {
                    if (!cancelReason.trim() || !interview || cancelling) return;
                    setCancelling(true);
                    try {
                      const resp = await fetch('/api/candidate/interview/cancel', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({
                          cancellation_reason: cancelReason.trim(),
                          interviewId: interview.id,
                        }),
                      });
                      if (!resp.ok) {
                        const err = await resp.json().catch(() => ({}));
                        throw new Error(err.error || 'Failed to cancel');
                      }
                      await fetchCandidateInterview();
                      toast.success('Interview cancelled.');
                      closeCancelModal();
                    } catch (e) {
                      toast.error(e instanceof Error ? e.message : 'Failed to cancel interview');
                    } finally {
                      setCancelling(false);
                    }
                  }}
                >
                  {cancelling ? 'Cancelling…' : 'Confirm cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAuthenticated && email && (
        <AgentChatWidget mode="candidate" participantName={email} startButtonLabel="Ask AI" />
      )}
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={<DashboardFallback />}>
      <DashboardContent />
    </Suspense>
  );
}
